import { Avatar, Button, Checkbox, Divider, List, ListItem, ListItemAvatar, ListItemText, Paper, Snackbar, Tab, Tabs, TextField, Typography, ListItemSecondaryAction, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Icon, IconButton, TableCell, TableRow, Table, TableBody, TableContainer } from '@material-ui/core'
import { Alert, Autocomplete } from '@material-ui/lab'
import { ManagementContext } from 'context'
import { ROOT_TITLE, EXCEPTION_TITLE, EXCEPTION_TYPES, createExceptionService, getExceptionsService, deleteExceptionService, getSupplierFileByIdService, getSuppliersFileService, COUNTRIES, FIELDS, INVOICE_PAYMENT_TYPES, PAYMENT_TYPES, REGIMEN_TYPES, SUJETO_RETENIDO_TYPES, SUSTENTO_CODES } from 'lib'
import React from 'react'
import { Helmet } from 'react-helmet'
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank"
import CheckBoxIcon from "@material-ui/icons/CheckBox"
import { IException, IIdentificationType, IMessageConfig, ISupplier, ISupplierFile, ITaxPlan } from 'types'
import { ConfirmDialog, FixedLoadingIndicator, FullSizeDialog, Search, SupplierForm } from 'components'
import { useInputValue } from 'hooks'
import { useHistory, useLocation } from 'react-router-dom'

interface Props {

}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkedIcon = <CheckBoxIcon fontSize="small" />

const Supplier = ({ supplier, exception, taxPlanName, setDeleteId }: { supplier: ISupplier | undefined, exception: IException, taxPlanName?: string, setDeleteId: React.Dispatch<React.SetStateAction<number>> }) => {
    return (
        <ListItem>
            <ListItemAvatar>
                <Avatar>
                    {supplier?.comercialName.charAt(0).toUpperCase()}
                </Avatar>
            </ListItemAvatar>
            <ListItemText
                primary={`${supplier?.comercialName}`}
                secondary={
                    <React.Fragment>
                        <span style={{ display: "block" }}>{supplier?.identification}</span>
                        <span style={{ display: "block" }}>
                            {`Plan Almaceneras > ${taxPlanName ?? "No configurado"}`}
                        </span>
                    </React.Fragment>
                }
            />
            <ListItemSecondaryAction>
                <Button onClick={() => setDeleteId(exception.exceptionId)} variant="contained" disableElevation size="small">
                    <Typography className="px-2" variant="caption">
                        {"Quitar de la lista de excepciones"}
                    </Typography>
                </Button>
            </ListItemSecondaryAction>
        </ListItem>
    )
}

interface SupplierInformationDialogProps {
    open: boolean
    supplier?: ISupplier
    identificationTypes: IIdentificationType[]
    loading: boolean
    onCancel: () => void
    onAccept: () => void
    onEdit: () => void
}

interface InfoItemProps {
    label: string;
    value?: React.ReactNode;
    fullWidth?: boolean;
}

const InfoItem = ({ label, value, fullWidth = false }: InfoItemProps) => (
    <Grid item xs={12} sm={fullWidth ? 12 : 6}>
        <div
            style={{
                height: "100%",
                padding: "12px 16px",
                borderRadius: 8,
                backgroundColor: "#f7f8fa",
                border: "1px solid #e5e7eb"
            }}
        >
            <Typography
                variant="caption"
                color="textSecondary"
                style={{
                    display: "block",
                    marginBottom: 4,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em"
                }}
            >
                {label}
            </Typography>

            <Typography
                variant="body2"
                style={{
                    color: value ? "#1f2937" : "#9ca3af",
                    fontWeight: value ? 500 : 400,
                    wordBreak: "break-word"
                }}
            >
                {value || "No registrado"}
            </Typography>
        </div>
    </Grid>
);

const SupplierInformationDialog = ({
    open,
    supplier,
    identificationTypes,
    loading,
    onCancel,
    onAccept,
    onEdit
}: SupplierInformationDialogProps) => {
    
    const getIdentificationTypeName = (value: any) => {
        const identificationType = identificationTypes.find(
            item =>
                item.identificationTypeId.toString() ===
                value?.toString()
        )

        return identificationType?.description ?? value ?? ""
    }

    const getCatalogName = (
        catalog: Array<{ value: any, name: string }>,
        value: any
    ) => {
        if (value === undefined || value === null || value === "") {
            return ""
        }

        const option = catalog.find(
            item => item.value?.toString() === value?.toString()
        )

        return option?.name ?? value
    }

    const getCountryName = (value: any) => {
        if (value === undefined || value === null || value === "") {
            return ""
        }

        const country = COUNTRIES.find(
            item => item.value?.toString() === value?.toString()
        )

        return country
            ? `${country.value} - ${country.name}`
            : value
    }

    const ReadOnlyField = ({
        label,
        value
    }: {
        label: string
        value: any
    }) => (
        <Grid item xs={12} sm={6}>
            <TextField
                label={label}
                value={value ?? ""}
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{
                    readOnly: true
                }}
                InputLabelProps={{
                    shrink: true
                }}
            />
        </Grid>
    )

    const supplierValue = (field: { key: string }) => {
        return supplier
            ? (supplier as any)[field.key]
            : ""
    }

    // Componente para filas de la tabla
    const InfoRow = ({ label, value }: { label: string; value?: string | React.ReactNode }) => (
        <TableRow style={{ borderBottom: "1px solid #f3f4f6" }}>
            <TableCell
                component="th"
                scope="row"
                style={{
                    padding: "6px 8px",
                    fontWeight: 600,
                    color: "#6b7280",
                    fontSize: "0.8125rem",
                    width: "40%",
                    borderBottom: "none"
                }}
            >
                {label}
            </TableCell>
            <TableCell
                style={{
                    padding: "6px 8px",
                    color: "#111827",
                    fontSize: "0.8125rem",
                    fontWeight: 500,
                    wordBreak: "break-word",
                    borderBottom: "none"
                }}
            >
                {value || "-"}
            </TableCell>
        </TableRow>
    );

    return (
        <Dialog
            open={open}
            onClose={loading ? undefined : onCancel}
            fullWidth
            maxWidth="md"
        >
            <DialogTitle style={{ padding: "12px 20px" }}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center">
                        <div
                            style={{
                                width: 32,
                                height: 32,
                                marginRight: 10,
                                borderRadius: 6,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#1976d2",
                                backgroundColor: "#eaf3fb"
                            }}
                        >
                            <Icon style={{ fontSize: 20 }}>business</Icon>
                        </div>

                        <div>
                            <Typography variant="subtitle1" style={{ fontWeight: 600, lineHeight: 1.2 }}>
                                Información del proveedor
                            </Typography>

                            <Typography variant="caption" color="textSecondary">
                                Revise los datos antes de continuar
                            </Typography>
                        </div>
                    </div>

                    <IconButton
                        size="small"
                        onClick={onCancel}
                        disabled={loading}
                        aria-label="Cerrar"
                    >
                        <Icon fontSize="small">close</Icon>
                    </IconButton>
                </div>
            </DialogTitle>

            <DialogContent dividers style={{ padding: "16px 20px" }}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: 16,
                        padding: "8px 12px",
                        borderRadius: 6,
                        color: "#174a75",
                        backgroundColor: "#edf6fd",
                        border: "1px solid #cbe5f8"
                    }}
                >
                    <Icon
                        fontSize="small"
                        style={{ marginRight: 8, fontSize: 18 }}
                    >
                        info
                    </Icon>

                    <Typography variant="caption" style={{ lineHeight: 1.3 }}>
                        Esta información es únicamente de consulta. Si encuentra algún
                        dato incorrecto, seleccione <strong>Modificar</strong> antes de
                        agregar al proveedor a la lista de excepciones.
                    </Typography>
                </div>

                <Typography
                    variant="caption"
                    style={{
                        display: "block",
                        marginBottom: 4,
                        fontWeight: 700,
                        color: "#374151",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em"
                    }}
                >
                    Datos generales
                </Typography>

                <TableContainer style={{ marginBottom: 16 }}>
                    <Table size="small">
                        <TableBody>
                            <InfoRow
                                label="Identificación"
                                value={supplier?.identification}
                            />
                            <InfoRow
                                label="Tipo de identificación"
                                value={getIdentificationTypeName(
                                    supplier?.identificationType
                                )}
                            />
                            <InfoRow
                                label="Razón social"
                                value={supplier?.companyName}
                            />
                            <InfoRow
                                label="Correo Receptor"
                                value={supplier?.email}
                            />
                        </TableBody>
                    </Table>
                </TableContainer>

                <Typography
                    variant="caption"
                    style={{
                        display: "block",
                        marginBottom: 4,
                        fontWeight: 700,
                        color: "#374151",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em"
                    }}
                >
                    Información tributaria y de pago
                </Typography>

                <TableContainer>
                    <Table size="small">
                        <TableBody>
                            <InfoRow
                                label="Parte relacionada"
                                value={supplier?.relatedPart ? "Sí" : "No"}
                            />
                            <InfoRow
                                label="Forma de pago"
                                value={getCatalogName(
                                    INVOICE_PAYMENT_TYPES,
                                    supplierValue(FIELDS.invoicePaymentType)
                                )}
                            />
                            <InfoRow
                                label="Código Sustento"
                                value={getCatalogName(
                                    SUSTENTO_CODES,
                                    supplierValue(FIELDS.sustentoCode)
                                )}
                            />
                            <InfoRow
                                label="Tipo de pago"
                                value={getCatalogName(
                                    PAYMENT_TYPES,
                                    supplierValue(FIELDS.paymentType)
                                )}
                            />

                            {supplier?.identificationTypeCode === '08' && (
                                <>
                                    {console.log('Supplier actual:', supplier)}
                                    <InfoRow
                                        label="Tipo de sujeto retenido"
                                        value={getCatalogName(
                                            SUJETO_RETENIDO_TYPES,
                                            supplier?.sujetoRetenidoType // o supplierValue(FIELDS.sujetoRetenidoType)
                                        )}
                                    />
                                </>
                            )}
                            {/* Campos dependientes: se muestran únicamente si paymentType NO es "01" (Exterior) */}
                            {supplierValue(FIELDS.paymentType) !== "01" && (
                                <React.Fragment>
                                    <InfoRow
                                        label="Tipo de régimen"
                                        value={getCatalogName(
                                            REGIMEN_TYPES,
                                            supplierValue(FIELDS.regimenType)
                                        )}
                                    />
                                    <InfoRow
                                        label="País al que se efectúa el pago"
                                        value={getCountryName(
                                            supplierValue(FIELDS.countryPayment)
                                        )}
                                    />
                                    <InfoRow
                                        label="Aplica convenio de doble tributación"
                                        value={supplierValue(FIELDS.doubleTribCon)}
                                    />

                                    {/* Se renderiza solo si doubleTribCon es "NO" */}
                                    {supplierValue(FIELDS.doubleTribCon) === "NO" && (
                                        <InfoRow
                                            label="Pago sujeto a retención por norma legal"
                                            value={supplierValue(FIELDS.legalNormPayment)}
                                        />
                                    )}

                                    <InfoRow
                                        label="Régimen fiscal preferente o de menor imposición"
                                        value={supplierValue(FIELDS.fiscalRegimen)}
                                    />
                                </React.Fragment>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>

            <DialogActions style={{ padding: "10px 20px" }}>
                <Button
                    onClick={onCancel}
                    disabled={loading}
                    size="small"
                    style={{ marginRight: "auto" }}
                >
                    Cancelar
                </Button>

                <Button
                    onClick={onEdit}
                    color="primary"
                    variant="outlined"
                    size="small"
                    disableElevation
                    disabled={loading}
                    startIcon={<Icon fontSize="small">edit</Icon>}
                >
                    Modificar
                </Button>

                <Button
                    onClick={onAccept}
                    color="primary"
                    variant="contained"
                    size="small"
                    disableElevation
                    disabled={loading || !supplier}
                    startIcon={<Icon fontSize="small">check</Icon>}
                >
                    Aceptar
                </Button>
            </DialogActions>
        </Dialog>
    )
}

const Exceptions = (props: Props) => {

    const history = useHistory()
    const location = useLocation()

    const [selectedSupplierId, setSelectedSupplierId] =
        React.useState<number | undefined>(undefined)

    const [openSupplierInformation, setOpenSupplierInformation] =
        React.useState<boolean>(false)

    const [openSupplierEdit, setOpenSupplierEdit] =
        React.useState<boolean>(false)

    const [openTaxPlanSelection, setOpenTaxPlanSelection] =
        React.useState<boolean>(false)

    const [selectedTaxPlan, setSelectedTaxPlan] =
        React.useState<ITaxPlan | null>(null)

    const [type, setType] = React.useState(0)
    const {
        suppliers,
        refreshSuppliers,
        identificationTypes,
        taxPlans,
    } = React.useContext(ManagementContext)

    const selectedSupplier = suppliers.find(
        supplier => supplier.supplierId === selectedSupplierId
    )
    const [loading, setLoading] = React.useState<boolean>(false)
    const [messageConfig, setMessageConfig] = React.useState<IMessageConfig>({ open: false, message: "", severity: "info" })
    const [deleteId, setDeleteId] = React.useState<number>(-1)
    const [list, setList] = React.useState<IException[]>([])
    const { value: query, clearValue: handleClear, onChange: handleChange } = useInputValue()

    const handleAdd = async (selected: ISupplier, planId: number): Promise<boolean> => {
        try {
            setLoading(true)

            await createExceptionService({
                supplierId: selected.supplierId,
                type,
                planId
            })

            await getExceptions()

            setMessageConfig({
                open: true,
                message: "Proveedor agregado a lista de excepciones!",
                severity: "success"
            })

            setLoading(false)
            return true
        } catch (error) {
            setLoading(false)

            setMessageConfig({
                open: true,
                message: "No se pudo agregar el proveedor seleccionado.",
                severity: "error"
            })

            return false
        }
    }

    const getExceptions = React.useCallback(async () => {
        try {
            setLoading(true)
            const result = await getExceptionsService()
            setList(result)
            setLoading(false)
        } catch (error) {
            setLoading(false)
        }
    }, [])

    const getExceptionsByType = React.useCallback(() => {
        return list.filter(item => item.type === type)
    }, [list, type])

    const handleDelete = async () => {
        try {
            setLoading(true)
            await deleteExceptionService(deleteId)
            setLoading(false)
            await getExceptions()
            setMessageConfig({ open: true, message: "Regla de excepción eliminada!", severity: "success" })
            return true
        } catch (error) {
            setMessageConfig({ open: true, message: "No se pudo eliminar la excepción...!", severity: "error" })
            setLoading(false)
            return false
        }
    }

    React.useEffect(() => {
        getExceptions()
    }, [getExceptions])

    const handleSelectSupplier = (supplier: ISupplier) => {
        setSelectedSupplierId(supplier.supplierId)
        setOpenSupplierInformation(true)
    }

    const handleCancelSupplier = () => {
        setOpenSupplierInformation(false)
        setSelectedSupplierId(undefined)
    }

    const handleAcceptSupplier = () => {
        if (!selectedSupplier) {
            return
        }

        setOpenSupplierInformation(false)
        setSelectedTaxPlan(null)
        setOpenTaxPlanSelection(true)
    }

    const handleCancelTaxPlanSelection = () => {
        setOpenTaxPlanSelection(false)
        setSelectedTaxPlan(null)
        setSelectedSupplierId(undefined)
    }

    const handleConfirmTaxPlanSelection = async () => {
        if (!selectedSupplier || !selectedTaxPlan) {
            return
        }

        const added = await handleAdd(selectedSupplier, selectedTaxPlan.taxPlanId)

        if (added) {
            setOpenTaxPlanSelection(false)
            setSelectedTaxPlan(null)
            setSelectedSupplierId(undefined)
        }
    }

    const handleEditSupplier = async () => {
        if (!selectedSupplier) {
            return
        }

        try {
            setLoading(true)

            const files: ISupplierFile[] =
                await getSuppliersFileService(selectedSupplier.supplierId)

            for (let i = 0; i < files.length; i++) {
                const file = await getSupplierFileByIdService(
                    files[i].supplierFileId ?? -1
                )

                files[i] = {
                    ...files[i],
                    file: {
                        ...file,
                        name: files[i].fileName
                    }
                }
            }

            history.replace({
                pathname: location.pathname,
                state: {
                    edit: true,
                    values: selectedSupplier,
                    files
                }
            })

            setOpenSupplierInformation(false)
            setOpenSupplierEdit(true)
            setLoading(false)
        } catch (error) {
            setLoading(false)

            setMessageConfig({
                open: true,
                message: "No se pudo obtener la información del proveedor.",
                severity: "error"
            })
        }
    }

    const handleCloseSupplierEdit = () => {
        history.replace(location.pathname, undefined)

        setOpenSupplierEdit(false)

        // Regresa al diálogo informativo.
        setOpenSupplierInformation(true)
    }

    const handleSupplierUpdated = async () => {
        await refreshSuppliers()

        setMessageConfig({
            open: true,
            message: "Proveedor actualizado correctamente.",
            severity: "success"
        })
    }

    return (
        <Paper className="flex flex-col h-full overflow-hidden pb-0">
            <Helmet>
                <title>{`${ROOT_TITLE} - ${EXCEPTION_TITLE}`}</title>
            </Helmet>
            <div className="flex w-full items-center">
                <Tabs
                    value={type}
                    onChange={(_, newValue) => {
                        setType(newValue)
                        handleClear()
                    }}
                    textColor='primary'
                    indicatorColor='primary'
                    centered
                    style={{ width: "68%" }}
                    variant="fullWidth"
                >
                    {
                        EXCEPTION_TYPES.map(exception => (
                            <Tab label={exception.name} />
                        ))
                    }
                </Tabs>
                <Autocomplete
                    multiple
                    options={suppliers.filter(s => !s.employee && !list.map(e => e.supplierId).includes(s.supplierId))}
                    disableCloseOnSelect
                    getOptionLabel={(supplier) => `${supplier.companyName}`}
                    limitTags={2}
                    value={suppliers.filter(s => getExceptionsByType().map(e => e.supplierId).includes(s.supplierId))}
                    openOnFocus
                    disableClearable
                    size="small"
                    style={{ width: "28%", marginLeft: "3%" }}
                    ChipProps={{ size: "small", style: { maxWidth: 90 } }}
                    renderTags={() => <React.Fragment />}
                    disabled={loading}
                    onChange={(_, _values, reason, value) => {
                        if (reason === "select-option") {
                            const selected = value?.option
                            if (selected) {
                                handleSelectSupplier(selected)
                            }
                        }
                    }}
                    renderOption={(supplier, { selected }) => {
                        return (
                            <React.Fragment>
                                <Checkbox
                                    icon={icon}
                                    checkedIcon={checkedIcon}
                                    style={{ marginRight: 8, height: 50 }}
                                    checked={selected}
                                    disabled={loading}
                                />
                                {`${supplier.companyName}`}
                            </React.Fragment>
                        )
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            fullWidth
                            placeholder={"Selecciona para agregar"}
                        />
                    )}
                />
            </div>
            <div className="w-full">
                <Divider />
            </div>
            <div className="w-full px-4 py-4 flex justify-between items-center">
                <Search
                    onChange={handleChange}
                    query={query}
                    onClear={handleClear}
                    placeholer="Nombre o identificación"
                />
                <Typography
                    color='textSecondary'
                    align='right'
                >
                    {EXCEPTION_TYPES[type]?.description}
                </Typography>
            </div>
            <div className="flex h-full flex-col overflow-hidden">
                <div className="flex flex-grow overflow-auto">
                    <List style={{ width: '100%' }}>
                        {
                            getExceptionsByType().map(exception => {
                                const supplier = suppliers.find(s => s.supplierId === exception.supplierId)
                                const taxPlanName = taxPlans.find(t => t.taxPlanId === exception.planId)?.name
                                return (
                                    <React.Fragment>
                                        {
                                            Boolean(query) ?
                                                <React.Fragment>
                                                    {
                                                        (supplier?.identification.includes(query) || supplier?.comercialName?.toUpperCase().includes(query.toUpperCase())) ?
                                                            <Supplier
                                                                supplier={supplier}
                                                                exception={exception}
                                                                taxPlanName={taxPlanName}
                                                                setDeleteId={setDeleteId}
                                                            />
                                                            :
                                                            undefined
                                                    }
                                                </React.Fragment>
                                                :
                                                <Supplier
                                                    supplier={supplier}
                                                    exception={exception}
                                                    taxPlanName={taxPlanName}
                                                    setDeleteId={setDeleteId}
                                                />
                                        }
                                    </React.Fragment>
                                )
                            })
                        }
                    </List>
                </div>
            </div>

            <SupplierInformationDialog
                open={openSupplierInformation}
                supplier={selectedSupplier}
                identificationTypes={identificationTypes}
                loading={loading}
                onCancel={handleCancelSupplier}
                onAccept={handleAcceptSupplier}
                onEdit={handleEditSupplier}
            />
            <Dialog
                open={openTaxPlanSelection}
                onClose={(_, reason) => {
                    if (reason === "backdropClick" && loading) {
                        return
                    }
                    handleCancelTaxPlanSelection()
                }}
                disableEscapeKeyDown={loading}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Seleccionar plan de impuestos</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                        {`Selecciona el plan de impuestos para ${selectedSupplier?.companyName ?? "el proveedor"}.`}
                    </Typography>
                    <Autocomplete
                        options={taxPlans}
                        getOptionLabel={(taxPlan) => taxPlan.name}
                        getOptionSelected={(option, value) => option.taxPlanId === value.taxPlanId}
                        value={selectedTaxPlan}
                        onChange={(_, value) => setSelectedTaxPlan(value)}
                        openOnFocus
                        disabled={loading}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                autoFocus
                                variant="outlined"
                                fullWidth
                                label="Plan de impuestos"
                                required
                            />
                        )}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCancelTaxPlanSelection}
                        disabled={loading}
                        size="small"
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleConfirmTaxPlanSelection}
                        color="primary"
                        variant="contained"
                        size="small"
                        disableElevation
                        disabled={loading || !selectedTaxPlan}
                    >
                        Agregar
                    </Button>
                </DialogActions>
            </Dialog>
            <FullSizeDialog
                open={openSupplierEdit}
                onClose={handleCloseSupplierEdit}
            >
                <SupplierForm
                    onSuccess={handleSupplierUpdated}
                    onError={() => {
                        setMessageConfig({
                            open: true,
                            message: "No se pudo actualizar el proveedor.",
                            severity: "error"
                        })
                    }}
                    onClose={handleCloseSupplierEdit}
                />
            </FullSizeDialog>


            <Snackbar open={messageConfig.open} autoHideDuration={6000} onClose={() => setMessageConfig({ ...messageConfig, open: false })} anchorOrigin={{ horizontal: "right", vertical: "bottom" }}>
                <Alert variant="filled" onClose={() => setMessageConfig({ ...messageConfig, open: false })} severity={messageConfig.severity}>
                    {messageConfig.message}
                </Alert>
            </Snackbar>
            <FixedLoadingIndicator loading={loading} />
            <ConfirmDialog
                onCancel={() => setDeleteId(-1)}
                onConfirm={handleDelete}
                open={Boolean(deleteId !== -1)}
                title={"¿Estás seguro que deseas eliminar?"}
                loading={loading}
            />
        </Paper>
    )
}

export default Exceptions
